import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageCircle, Eye, Share2, Bookmark, MoreHorizontal, X, ChevronLeft, ChevronRight, Repeat2, MessageSquareQuote, ArrowLeft, ActivitySquare, Trash2, Send, Pin } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { Loop, useLoopActions, useLoopComments } from '@/hooks/use-loops';
import { cn } from '@/lib/utils';
import { getThumbnailUrl } from '@/utils/imageUtils';
import LoopComments from './LoopComments';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getDarkGradient, getStringHash } from '@/utils/anonymousAvatars';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useReblogActions, ReblogAnnotation } from '@/hooks/use-reblogs';
import TrailLensDialog from './TrailLensDialog';
import { useLoopCommunities, useCommunityInteraction } from '@/hooks/use-loop-communities';
import { CommunityBadges } from './CommunityBadges';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useAuthorMediaLoops } from '@/hooks/use-author-media-loops';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSignature } from '@/hooks/use-signature';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import AmbientBackdrop from '@/components/common/AmbientBackdrop';
import { CreatorSignature } from '@/components/profile/CreatorSignature';
import { AnnotationPinsOverlay } from '@/components/annotations/AnnotationPinsOverlay';
import { AnnotationVisibilityToggle } from '@/components/annotations/AnnotationVisibilityToggle';
import { usePostAnnotations } from '@/hooks/use-annotations';
import { PostAnnotationsManagerWithPins } from '@/components/posts/PostAnnotationsManagerWithPins';

interface ExpandedLoopPostProps {
  loop: Loop;
  onClose: () => void;
  className?: string;
}

const ExpandedLoopPost: React.FC<ExpandedLoopPostProps> = ({
  loop,
  onClose,
  className
}) => {
  const [showComments, setShowComments] = useState(false);
  const { likeLoop, unlikeLoop, viewLoop, isLiking, isUnliking } = useLoopActions();
  const { reblog, quote, remix, isReblogging } = useReblogActions();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [trailOpen, setTrailOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [newComment, setNewComment] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { addComment, isCommenting, deleteLoop } = useLoopActions();
  const { data: comments, isLoading: commentsLoading } = useLoopComments(loop?.id);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const hasMedia = !!(loop?.image_url || loop?.video_url || loop?.thumbnail_url);
  const { data: authorMedia, loading: loadingAuthorMedia } = useAuthorMediaLoops(loop?.user_id, loop?.id, 12);
  const [artstream, setArtstream] = useState<Array<{ id: string; image_url?: string | null; thumbnail_url?: string | null }>>([]);
  const { data: authorSignature } = useSignature(loop?.user_id);
  const { annotations, isLoading: annotationsLoading } = usePostAnnotations(loop?.id, 'loop', {
    includeSeen: false,
    enabled: true,
  });
  const [annotationManagerOpen, setAnnotationManagerOpen] = useState(false);
  
  // Fetch communities for this loop
  const { data: loopCommunities } = useLoopCommunities(loop.id);
  const { trackInteraction } = useCommunityInteraction();
  
  // Check if current user can delete this post (admin or author)
  const canDelete = useMemo(() => {
    if (!user) return false;
    const isAuthor = user.id === loop.user_id;
    const isAdmin = !!profile?.is_admin;
    return isAuthor || isAdmin; // Backend also validates
  }, [user, loop.user_id, profile?.is_admin]);

  // Check if current user is the author (for annotation management)
  const isAuthor = useMemo(() => {
    if (!user) return false;
    return user.id === loop.user_id;
  }, [user, loop.user_id]);

  // Record a view when expanded
  useEffect(() => {
    if (loop?.id) {
      viewLoop(loop.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loop?.id]);

  // Fetch discovery artstream for desktop sidebar (simple recent media bulletins)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const { data, error } = await supabase
          .from('bulletins')
          .select('id,image_url,thumbnail_url,moderation_status')
          .or('image_url.not.is.null,thumbnail_url.not.is.null' as any)
          .or('moderation_status.in.(approved,flagged),moderation_status.is.null' as any)
          .order('created_at', { ascending: false })
          .limit(24);
        if (!error && !cancelled) {
          setArtstream((data || []) as any);
        }
      } catch (e) {
        // no-op
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);
  
  const handleLike = () => {
    if (loop.is_liked) {
      unlikeLoop(loop.id);
    } else {
      likeLoop(loop.id);
    }
  };

  // Twemoji pixelated helper
  const emojiToCodepoints = (emoji: string) => Array.from(emoji || '').map(c => c.codePointAt(0)!.toString(16)).join('-');
  const twemojiUrl = (emoji?: string) => (emoji ? `https://cdn.jsdelivr.net/gh/twitter/twemoji@latest/assets/72x72/${emojiToCodepoints(emoji)}.png` : '');

  // Fetch adopted community for this author if they have one
  const adoptedCommunityId = (loop.user as any)?.community_tag_id as string | undefined;
  const { data: adoptedCommunity } = useQuery({
    queryKey: ['adopted-community', adoptedCommunityId],
    enabled: !!adoptedCommunityId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('id, name, short_tag, emote')
        .eq('id', adoptedCommunityId!)
        .single();
      if (error) throw error;
      return data as { id: string; name: string; short_tag?: string | null; emote?: string | null };
    }
  });

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      addComment({ loopId: loop.id, content: newComment });
      setNewComment('');
    }
  };
  
  // Handle delete post button click
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    deleteLoop({
      loopId: loop.id,
      userId: loop.user_id
    });
    setDeleteDialogOpen(false);
    onClose(); // Close expanded view after deletion
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0.8, height: "auto" }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0.8, height: "auto" }}
        className={cn(
          "relative bg-[#1A1A1B] rounded-xl mb-4 overflow-hidden",
          "flex flex-col w-full max-w-6xl mx-auto",
          className
        )}
      >
        {/* Close button and back to feed */}
        <div className="sticky top-0 right-0 w-full flex justify-between items-center p-3 z-20 bg-gradient-to-b from-[#1A1A1B] to-transparent">
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-sm rounded-lg px-3 py-1 bg-[#272729] hover:bg-[#343536]"
            onClick={onClose}
          >
            <ArrowLeft size={16} />
            <span>Back to feed</span>
          </Button>
        </div>
        
        {/* Content */}
        <div className="p-4 pt-0">
          {/* Header section with user info */}
          <div className="relative pb-4 border-b border-[#343536] flex items-center">
            <div 
              onClick={(e) => {
                e.stopPropagation();
                if (loop.user?.username) {
                  navigate(`/user/${loop.user.username}`);
                }
              }}
              className="cursor-pointer"
            >
              <Avatar className={cn(
                "h-10 w-10 mr-3 rounded-full overflow-hidden",
                (loop.user as any)?.is_admin ? "ring-2 ring-[#F97316]" : (loop.user as any)?.is_premium ? "ring-2 ring-[#E5DEFF]" : "ring-2 ring-white/10"
              )}>
                <AvatarImage 
                  src={loop.user?.profile_image_url || ''} 
                  className="object-cover w-full h-full"
                />
                <AvatarFallback className="text-base">
                  {(loop.user?.username || 'U')[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>
            
            <div className="flex flex-col flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span 
                  className="font-medium hover:underline cursor-pointer flex items-center gap-1.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (loop.user?.username) {
                      navigate(`/user/${loop.user.username}`);
                    }
                  }}
                >
                  @{loop.user?.username || 'anonymous'}
                  {loop.user?.is_admin && (
                    <VerifiedBadge type="admin" size={18} className="inline-block" />
                  )}
                  {loop.user?.is_partner && (
                    <VerifiedBadge type="partner" size={18} className="inline-block" />
                  )}
                </span>
                {adoptedCommunity ? (
                  <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] leading-none">
                    {adoptedCommunity.emote ? (
                      <img src={twemojiUrl(adoptedCommunity.emote)} alt={adoptedCommunity.emote || ''} className="h-3.5 w-3.5" style={{ imageRendering: 'pixelated' as any }} />
                    ) : null}
                    <span className="font-mono tracking-widest">{(adoptedCommunity.short_tag || adoptedCommunity.name || '').toUpperCase()}</span>
                  </span>
                ) : (
                  loop.user?.tag_code ? (
                    <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] leading-none">
                      {loop.user?.tag_emote ? (
                        <img src={twemojiUrl(loop.user.tag_emote)} alt={loop.user.tag_emote} className="h-3.5 w-3.5" style={{ imageRendering: 'pixelated' as any }} />
                      ) : null}
                      <span className="font-mono tracking-widest">{loop.user.tag_code}</span>
                    </span>
                  ) : null
                )}
                {/* Community badges inline */}
                {loopCommunities && loopCommunities.length > 0 && (
                  <CommunityBadges 
                    communities={loopCommunities.map(lc => lc.community)}
                    variant="inline"
                    onCommunityClick={(community) => {
                      trackInteraction(community.id, 'view');
                      navigate(`/community/${community.name}`);
                    }}
                  />
                )}
              </div>
              <span className="text-xs text-[#818384]">
                {loop.user?.display_name || loop.user?.username || 'Anonymous'} • 
                {loop.created_at && formatDistanceToNow(new Date(loop.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>
          
          {/* Main grid: left content, optional right discovery on desktop */}
          <div className="py-4 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6">
            <div className="space-y-4">
              <div className="relative rounded-xl bg-[#171718] shadow-md p-4">
                {(() => {
                  const parts = loop.content?.split('\n\n') || [];
                  const title = parts[0];
                  const description = parts.slice(1).join('\n\n');

                  return (
                    <>
                      {title && <h2 className="text-xl font-semibold mb-3">{title}</h2>}
                      {description && <p className="whitespace-pre-wrap text-base text-gray-300">{description}</p>}
                      {!title && !description && <p className="whitespace-pre-wrap text-base">{loop.content}</p>}
                    </>
                  );
                })()}

                {(loop.image_url || loop.video_url || loop.thumbnail_url) && (
                  <div className="mt-4">
                    <div className="relative rounded-md overflow-hidden bg-[#151516]">
                      <AnnotationPinsOverlay postId={loop.id} postType="loop" />
                      <AmbientBackdrop imageUrl={loop.image_url || loop.thumbnail_url || undefined} />
                      {loop.image_url || loop.thumbnail_url ? (
                        <img
                          src={loop.image_url || loop.thumbnail_url}
                          alt="Post media"
                          className="relative max-w-full w-full object-contain z-[1]"
                        />
                      ) : (
                        <video
                          src={loop.video_url}
                          poster={loop.thumbnail_url}
                          className="relative max-w-full w-full object-contain z-[1]"
                          controls
                          playsInline
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {isMobile && hasMedia && (authorMedia?.length || 0) > 0 && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-[#D7DADC]">More from this creator</h3>
                    <div />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {authorMedia.slice(0, 9).map((m) => (
                      <button
                        key={m.id}
                        className="aspect-square overflow-hidden rounded-lg border border-[#343536] bg-[#151516]"
                        onClick={() => navigate(`/loops?post=${m.id}`)}
                      >
                        <img src={m.image_url || m.thumbnail_url || ''} alt="Creator post" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="py-2 border-t border-[#343536] text-xs text-[#818384]">
                <div className="flex items-center space-x-4">
                  <span>{loop.like_count || 0} likes</span>
                  <span>{loop.comment_count || 0} comments</span>
                  <span>{loop.reblog_count || 0} reposts</span>
                  <span>{loop.view_count || 0} views</span>
                </div>
              </div>

              <div className="py-2 border-t border-[#343536] grid grid-cols-5 gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn('flex items-center justify-center gap-1 py-3', loop.is_liked ? 'text-[#FF4500]' : 'text-[#D7DADC]')}
                  onClick={handleLike}
                >
                  <Heart size={20} className={loop.is_liked ? 'fill-[#FF4500]' : ''} />
                  <span className="text-xs">Like</span>
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center justify-center gap-1 py-3 text-[#D7DADC]"
                  onClick={() => reblog(loop.id)}
                >
                  <Repeat2 size={20} />
                  <span className="text-xs">Repost</span>
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center justify-center gap-1 py-3 text-[#D7DADC]"
                  onClick={() => {
                    onClose();
                    setTimeout(() => {
                      navigate(`/loops?post=${loop.id}&trail=true`);
                    }, 300);
                  }}
                >
                  <ActivitySquare size={20} />
                  <span className="text-xs">Trail</span>
                </Button>

                {isAuthor ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center justify-center gap-1 py-3 text-[#D7DADC]"
                    onClick={() => setAnnotationManagerOpen(true)}
                    aria-label="Manage annotations"
                    title="Manage annotations"
                  >
                    <Pin size={20} />
                    <span className="text-xs hidden sm:inline">Manage</span>
                  </Button>
                ) : (
                  <AnnotationVisibilityToggle
                    mode="icon"
                    align="end"
                    disabled={annotationsLoading || (annotations?.length ?? 0) === 0}
                    className="justify-self-center"
                  />
                )}

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center justify-center gap-1 py-3 text-[#D7DADC]"
                      aria-label="More options"
                    >
                      <MoreHorizontal size={20} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" side="bottom" sideOffset={8} className="w-auto p-0 bg-[#272729] border-[#343536] text-[#D7DADC]">
                    <button
                      className={
                        'w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#2D2D2F] ' +
                        (canDelete ? 'text-red-400' : 'text-[#8a8a8a] cursor-not-allowed')
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        if (!canDelete) return;
                        handleDeleteClick();
                      }}
                      title={canDelete ? 'Delete this post' : 'You can only delete your own posts or as admin'}
                    >
                      <Trash2 size={16} />
                      Delete post
                    </button>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="border-t border-[#343536] pt-4">
                <h3 className="font-medium mb-4">Comments</h3>
                <div className="mb-4">
                  <form onSubmit={handleSubmitComment} className="flex gap-2">
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="flex-1 bg-[#272729] border-[#343536]"
                      disabled={isCommenting}
                    />
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!newComment.trim() || isCommenting}
                      className="bg-[#272729] hover:bg-[#2D2D2F] text-[#D7DADC]"
                    >
                      <Send size={16} />
                    </Button>
                  </form>
                </div>
                <div className="space-y-3">
                  {commentsLoading ? (
                    <div className="text-center text-[#818384] py-2">Loading comments...</div>
                  ) : comments?.length === 0 ? (
                    <div className="text-center text-[#818384] py-4">No comments yet. Be the first to comment!</div>
                  ) : (
                    comments?.map((comment) => (
                      <div key={comment.id} className="flex gap-2">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            if (comment.user?.username) {
                              navigate(`/user/${comment.user.username}`);
                            }
                          }}
                          className="cursor-pointer"
                        >
                          <Avatar
                            className={cn(
                              'w-6 h-6 flex-shrink-0 rounded-full overflow-hidden',
                              (comment.user as any)?.is_admin ? 'ring-2 ring-[#F97316]' : (comment.user as any)?.is_premium ? 'ring-2 ring-[#E5DEFF]' : 'ring-1 ring-white/10'
                            )}
                          >
                            <AvatarImage src={comment.user?.profile_image_url} className="object-cover w-full h-full" />
                            <AvatarFallback className="text-xs">
                              {comment.user?.display_name?.[0] || comment.user?.username?.[0] || '?'}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="bg-[#272729] rounded-lg p-2">
                            <div
                              className="font-medium text-xs hover:underline cursor-pointer flex items-center gap-1.5 flex-wrap"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (comment.user?.username) {
                                  navigate(`/user/${comment.user.username}`);
                                }
                              }}
                            >
                              {comment.user?.display_name || comment.user?.username}
                              {comment.user?.is_admin && <VerifiedBadge type="admin" size={16} className="inline-block" />}
                              {comment.user?.is_partner && <VerifiedBadge type="partner" size={16} className="inline-block" />}
                              {comment.user?.tag_code && (
                                <span className="inline-flex items-center gap-1 rounded-md border px-1 py-0.5 text-[10px] leading-none">
                                  {comment.user?.tag_emote ? (
                                    <img src={twemojiUrl(comment.user.tag_emote)} alt={comment.user.tag_emote} className="h-3 w-3" style={{ imageRendering: 'pixelated' as any }} />
                                  ) : null}
                                  <span className="font-mono tracking-widest">{comment.user.tag_code}</span>
                                </span>
                              )}
                            </div>
                            <p className="text-sm mt-1">{comment.content}</p>
                          </div>
                          <div className="text-xs text-[#818384] mt-1">
                            {comment.created_at && formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="hidden lg:block lg:pl-6 lg:border-l lg:border-[#2B2C2E]">
              <div className="rounded-2xl bg-gradient-to-b from-[#121214] to-[#171718] p-3 ring-1 ring-white/5 shadow-[0_0_0_1px_rgba(255,255,255,0.03)] space-y-6">
                {hasMedia && (authorMedia?.length || 0) > 0 && (
                  <div>
                    <div className="flex items-center mb-2">
                      <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-300/80">More from this creator</h4>
                      <span className="flex-1 ml-3 h-px bg-white/10" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {authorMedia.map((m) => (
                        <button
                          key={m.id}
                          className="group aspect-square overflow-hidden rounded-xl bg-[#0F0F10] ring-1 ring-white/5 hover:ring-purple-500/30 hover:shadow-[0_10px_30px_-10px_rgba(168,85,247,0.35)] transition-all duration-200"
                          onClick={() => navigate(`/loops?post=${m.id}`)}
                          title={m.content || ''}
                        >
                          <img src={m.image_url || m.thumbnail_url || ''} alt="Creator post" className="w-full h-full object-cover scale-[1.01] group-hover:scale-105 transition-transform duration-300" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {artstream.length > 0 && (
                  <div>
                    <div className="flex items-center mb-2">
                      <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-300/80">From the Artstream</h4>
                      <span className="flex-1 ml-3 h-px bg-white/10" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {artstream.map((b) => (
                        <button
                          key={b.id}
                          className="group aspect-[3/4] overflow-hidden rounded-xl bg-[#0F0F10] ring-1 ring-white/5 hover:ring-purple-500/30 hover:shadow-[0_10px_30px_-10px_rgba(168,85,247,0.35)] transition-all duration-200"
                          onClick={() => navigate(`/bulletins?post=${b.id}`)}
                          aria-label="Open bulletin"
                        >
                          <img src={b.image_url || b.thumbnail_url || ''} alt="Bulletin" className="w-full h-full object-cover scale-[1.01] group-hover:scale-105 transition-transform duration-300" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {authorSignature && <CreatorSignature signature={authorSignature} />}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Annotation management dialog for authors */}
      <Dialog open={annotationManagerOpen} onOpenChange={setAnnotationManagerOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-[#1A1A1B] border-[#343536] text-[#D7DADC]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#E5E7EB]">Manage Annotations</DialogTitle>
          </DialogHeader>
          <PostAnnotationsManagerWithPins 
            postId={loop.id} 
            postType="loop"
            imageUrl={loop.image_url}
            thumbnailUrl={loop.thumbnail_url}
          />
        </DialogContent>
      </Dialog>
    
      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your post.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExpandedLoopPost;
